import { useMemo } from 'react';
import { useQuery, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { fetchBasketPricing } from '../services/basketPricing';
import { basketPricingInputFrom } from '../utils/pricedLines';
import { toMinorUnits } from '../utils/money';
import { useQuote } from '../context/QuoteContext';
import type { BasketPricingInput, PricedBasket } from '../types/basketPricing.types';

export const BASKET_PRICING_DEBOUNCE_MS = 250;

interface KeyedLine {
    readonly instanceId: string | number;
    readonly price: number;
    readonly gross_price: number;
}

/** Each line's amounts key alongside its id: a duration, MOT or date change reprices a visit
 *  while every instance id stays put. Minor units so float artefacts cannot split a key. */
const lineIdentities = (lines: readonly KeyedLine[]): string[] =>
    lines
        .map((line) =>
            JSON.stringify([line.instanceId, toMinorUnits(line.price), toMinorUnits(line.gross_price)]),
        )
        .sort();

/** An add-on's server figures also depend on which catalogue add-on the line is. */
const addOnLineIdentities = (lines: BasketPricingInput['addOns']): string[] =>
    lines
        .map((line) =>
            JSON.stringify([
                line.instanceId,
                line.addOnId,
                toMinorUnits(line.price),
                toMinorUnits(line.gross_price),
            ]),
        )
        .sort();

/** Keyed because a SMRiQ plan can change while its instance and session ids do not. Encoded
 *  structurally: vendor ids contain `:`, so a joined pair would not identify one pair. */
const declinedOperationIds = (services: BasketPricingInput['services']): string[] =>
    services
        .flatMap((service) =>
            (service.smriqInclusions?.declinedItems ?? []).map((item) =>
                JSON.stringify([service.instanceId, item.id]),
            ),
        )
        .sort();

/** Keyed by configured instance ids, not catalogue ids: two baskets holding the same
 *  services on different dates must not share a cache entry. */
export const basketPricingQueryKey = (input: BasketPricingInput) =>
    [
        'basket-price',
        input.productId,
        input.planId,
        input.servicePlanSessionId,
        lineIdentities(input.services),
        addOnLineIdentities(input.addOns),
        declinedOperationIds(input.services),
    ] as const;

/** The save path's options. QuoteErrorModal is that flow's sole 403 surface, so the global
 *  forbidden toast is suppressed here exactly as `createQuote`/`updateQuote` suppress it. */
export const basketPricingQueryOptions = (input: BasketPricingInput) =>
    queryOptions({
        queryKey: basketPricingQueryKey(input),
        queryFn: () => fetchBasketPricing(input, { suppressGlobalForbiddenToast: true }),
        staleTime: 30_000,
        retry: 1,
    });

export interface BasketPricingState {
    readonly data: PricedBasket | undefined;
    readonly isFetching: boolean;
    readonly isStale: boolean;
    /** False while the query is keyed on any basket other than the one on screen, so a
     *  reading taken from it describes a basket the user is no longer looking at. */
    readonly matchesInput: boolean;
    /** Covers the debounce window as well as the fetch, so a stale total is never shown
     *  without the accompanying announcement. */
    readonly isUpdating: boolean;
    readonly error: unknown;
    /** True when this basket is deliberately not priced at all, so a gate reading it must
     *  treat it as satisfied rather than waiting for a figure that will never arrive. */
    readonly notRequired: boolean;
    readonly refetch: () => void;
}

const DISABLED_QUERY_KEY = ['basket-price', 'disabled'] as const;

/** Debounces 250ms and keeps the previous total on screen while refetching; `isStale` covers
 *  both a lagging debounce and a kept-previous placeholder. */
export function useBasketPricing(
    requestedInput: BasketPricingInput | null,
    notRequired: boolean = false,
): BasketPricingState {
    // Not derived from a null input: that also means "no product selected yet", which must
    // keep blocking. Only the caller knows this basket is never to be priced at all.
    const input = notRequired ? null : requestedInput;
    const debouncedInput = useDebounce(input, BASKET_PRICING_DEBOUNCE_MS);

    const inputKey = useMemo(() => (input ? basketPricingQueryKey(input) : null), [input]);
    const debouncedKey = useMemo(
        () => (debouncedInput ? basketPricingQueryKey(debouncedInput) : null),
        [debouncedInput],
    );

    const query = useQuery<PricedBasket>({
        queryKey: debouncedKey ?? DISABLED_QUERY_KEY,
        queryFn: () => fetchBasketPricing(debouncedInput as BasketPricingInput),
        enabled: !notRequired && debouncedInput !== null,
        staleTime: 30_000,
        placeholderData: keepPreviousData,
    });

    const matchesInput =
        inputKey !== null && debouncedKey !== null && JSON.stringify(inputKey) === JSON.stringify(debouncedKey);

    const isStale = !matchesInput || query.isPlaceholderData;
    const isUpdating = input !== null && query.error === null && (isStale || query.isFetching);

    return {
        data: query.data,
        isFetching: query.isFetching,
        isStale,
        matchesInput,
        isUpdating,
        error: query.error,
        notRequired,
        refetch: () => {
            void query.refetch();
        },
    };
}

/** `enabled: false` disables the query entirely (no request) — for a quote that is no
 *  longer live, re-pricing against today's catalogue would show the wrong total. */
export function useQuoteBasketPricing(enabled: boolean = true): BasketPricingState {
    const { selectedProduct, resolvedPlanId, servicePlanSessionId, configuredServices, configuredAddOns } =
        useQuote();
    const input = useMemo(
        () =>
            basketPricingInputFrom({
                selectedProduct,
                resolvedPlanId,
                servicePlanSessionId,
                configuredServices,
                configuredAddOns,
            }),
        [selectedProduct, resolvedPlanId, servicePlanSessionId, configuredServices, configuredAddOns],
    );
    return useBasketPricing(input, !enabled);
}
